import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, Loader2, AlertCircle, Eye, EyeOff, Lock } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from '../contexts/TranslationContext';

import { API_BASE } from '../lib/apiBase';
const API = API_BASE;

export default function ResetPasswordPage() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isValidating, setIsValidating] = useState(true);
    const [tokenError, setTokenError] = useState(null);

    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setTokenError(t('auth.reset.no_token', 'No reset token provided'));
                setIsValidating(false);
                return;
            }

            try {
                await axios.get(`${API}/api/auth/verify-reset-token?token=${token}`);
                setIsValidating(false);
            } catch (error) {
                setTokenError(error.response?.data?.detail || t('auth.reset.invalid_link', 'Invalid or expired reset link'));
                setIsValidating(false);
            }
        };

        validateToken();
    }, [token, t]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password.length < 6) {
            toast.error(t('auth.reset.password_min_length', 'Password must be at least 6 characters'));
            return;
        }

        if (password !== confirmPassword) {
            toast.error(t('auth.reset.passwords_mismatch', 'Passwords do not match'));
            return;
        }

        setIsSubmitting(true);

        try {
            await axios.post(`${API}/api/auth/reset-password`, {
                token,
                new_password: password
            });
            setIsSuccess(true);
            toast.success(t('auth.reset.success_toast', 'Password reset successfully!'));
        } catch (error) {
            toast.error(error.response?.data?.detail || t('auth.reset.failed_toast', 'Failed to reset password'));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isValidating) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-500">{t('auth.reset.validating', 'Validating reset link...')}</p>
                </div>
            </div>
        );
    }

    if (tokenError) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8 flex flex-col items-center gap-2">
                        <img src="/owntune-logo-dark.png" alt="Owntune" className="h-8 w-auto" />
                    </div>

                    <Card className="shadow-premium-lg">
                        <CardContent className="pt-6">
                            <div className="text-center space-y-4">
                                <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                                    <AlertCircle className="h-8 w-8 text-red-600" />
                                </div>

                                <div>
                                    <h2 className="text-lg font-semibold text-slate-900">{t('auth.reset.invalid_title', 'Invalid Reset Link')}</h2>
                                    <p className="text-sm text-slate-500 mt-1">{tokenError}</p>
                                </div>

                                <div className="pt-4 space-y-3">
                                    <Link to="/forgot-password">
                                        <Button className="w-full bg-slate-900 hover:bg-slate-800">
                                            {t('auth.reset.request_new_link', 'Request New Reset Link')}
                                        </Button>
                                    </Link>
                                    <Link to="/login">
                                        <Button variant="outline" className="w-full">
                                            <ArrowLeft className="h-4 w-4 mr-2" />
                                            {t('auth.back_to_signin', 'Back to Sign In')}
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8 flex flex-col items-center gap-2">
                    <img src="/owntune-logo-dark.png" alt="Owntune" className="h-8 w-auto" />
                    <p className="text-sm text-slate-500">{t('brand.tagline', 'Vinyl Order Management')}</p>
                </div>

                <Card className="shadow-premium-lg">
                    <CardHeader className="text-center pb-2">
                        <CardTitle className="text-xl">
                            {isSuccess
                                ? t('auth.reset.success_title', 'Password Reset!')
                                : t('auth.reset.title', 'Set New Password')}
                        </CardTitle>
                        <CardDescription>
                            {isSuccess
                                ? t('auth.reset.success_desc', 'Your password has been changed successfully')
                                : t('auth.reset.subtitle', 'Enter your new password below')
                            }
                        </CardDescription>
                    </CardHeader>

                    <CardContent>
                        {isSuccess ? (
                            <div className="space-y-6 py-4">
                                <div className="flex justify-center">
                                    <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
                                        <CheckCircle className="h-8 w-8 text-emerald-600" />
                                    </div>
                                </div>

                                <Alert className="bg-emerald-50 border-emerald-200">
                                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                                    <AlertDescription className="text-emerald-700">
                                        {t('auth.reset.success_alert', 'Your password has been reset successfully. You can now sign in with your new password.')}
                                    </AlertDescription>
                                </Alert>

                                <Button
                                    className="w-full bg-slate-900 hover:bg-slate-800"
                                    onClick={() => navigate('/login')}
                                    data-testid="go-to-login-btn"
                                >
                                    {t('auth.signin_button', 'Sign In')}
                                </Button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4 pt-2">
                                <div className="space-y-2">
                                    <Label htmlFor="password">{t('auth.reset.new_password_label', 'New Password')}</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder={t('auth.reset.new_password_placeholder', 'Enter new password')}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="pl-10 pr-10"
                                            required
                                            minLength={6}
                                            autoFocus
                                            data-testid="new-password-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                    <p className="text-xs text-slate-500">{t('auth.reset.min_length_hint', 'Must be at least 6 characters')}</p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">{t('auth.reset.confirm_password_label', 'Confirm Password')}</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                        <Input
                                            id="confirmPassword"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder={t('auth.reset.confirm_password_placeholder', 'Confirm new password')}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="pl-10"
                                            required
                                            data-testid="confirm-password-input"
                                        />
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full bg-slate-900 hover:bg-slate-800"
                                    disabled={isSubmitting}
                                    data-testid="reset-password-btn"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            {t('auth.reset.resetting', 'Resetting...')}
                                        </>
                                    ) : (
                                        t('auth.reset.submit', 'Reset Password')
                                    )}
                                </Button>

                                <div className="text-center pt-2">
                                    <Link
                                        to="/login"
                                        className="text-sm text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1"
                                    >
                                        <ArrowLeft className="h-3 w-3" />
                                        {t('auth.back_to_signin', 'Back to Sign In')}
                                    </Link>
                                </div>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
